import React from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, Wallet, Zap, Clock } from 'lucide-react';

const features = [
  {
    icon: Gamepad2,
    kicker: '01',
    title: 'Earn through activities',
    desc: 'Play mini-games, try apps, take short surveys. Every completed action drops real rewards into your wallet.',
  },
  {
    icon: Wallet,
    kicker: '02',
    title: 'Offerwall-first experience',
    desc: 'A curated offerwall blended with fun — no dull forms, no endless waits. Pick what you love and get paid for it.',
  },
  {
    icon: Zap,
    kicker: '03',
    title: 'Fast, smooth, rewarding',
    desc: 'Built for zero friction — lightning-fast payouts, clean UI, and instant gratification on every tap.',
  },
  {
    icon: Clock,
    kicker: '04',
    title: 'Idle time, well spent',
    desc: 'Waiting for a bus, standing in line, lying in bed? Those quiet minutes now quietly pay you back.',
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-28 md:py-36 px-5 md:px-10 bg-surface-muted"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="font-pixel text-[11px] tracking-[0.25em] uppercase text-brand">
            About CoinsFlu
          </span>
          <h2
            data-testid="about-heading"
            className="mt-4 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tightest leading-[1.02]"
          >
            An earning app that actually feels <em className="not-italic text-brand">fun</em>.
          </h2>
          <p className="mt-6 text-ink/60 text-lg leading-relaxed max-w-2xl">
            We re-engineered the reward experience from the ground up — so the
            moment you open CoinsFlu, your time starts paying back.
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <motion.article
              key={f.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`about-feature-${i + 1}`}
              className="group relative p-7 rounded-3xl bg-white border border-black/5 hover:border-brand/30 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(229,9,20,0.25)] transition-all duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-brand/10 text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition-colors">
                  <f.icon size={22} strokeWidth={1.8} />
                </div>
                <span className="font-pixel text-[10px] tracking-[0.2em] text-ink/40">
                  {f.kicker}
                </span>
              </div>

              <h3 className="mt-6 text-lg font-semibold text-ink leading-tight">
                {f.title}
              </h3>
              <p className="mt-2 text-[14.5px] text-ink/55 leading-relaxed">
                {f.desc}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
